import { useCallback, useLayoutEffect, useRef, useState, type ComponentPropsWithRef } from 'react';

import { cx } from '@/shared/lib/cx';
import { useFieldContext } from '@/shared/ui/Field/FieldContext';

import styles from './Textarea.module.scss';

export interface TextareaProps extends ComponentPropsWithRef<'textarea'> {
  /** Счётчик символов. Осмыслен вместе с `maxLength`. */
  showCount?: boolean;
  /** Расти по содержимому вместо полосы прокрутки внутри поля. */
  autoGrow?: boolean;
  /** Предел роста в строках, дальше появляется прокрутка. */
  maxRows?: number;
  invalid?: boolean;
}

export function Textarea({
  showCount = false,
  autoGrow = false,
  maxRows = 12,
  invalid,
  className,
  id,
  disabled,
  required,
  maxLength,
  value,
  defaultValue,
  onChange,
  rows = 4,
  ref,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: TextareaProps) {
  const field = useFieldContext();
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const [length, setLength] = useState(() => String(value ?? defaultValue ?? '').length);

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled ?? false;
  const currentLength = value !== undefined ? String(value).length : length;

  const resize = useCallback(() => {
    const node = innerRef.current;

    if (!node || !autoGrow) {
      return;
    }

    // Высоту нужно сбросить до пересчёта: иначе scrollHeight помнит прежний
    // размер и поле умеет только расти, но не уменьшаться при удалении текста.
    node.style.height = 'auto';

    const lineHeight = parseFloat(getComputedStyle(node).lineHeight) || 20;
    const max = lineHeight * maxRows;

    node.style.height = `${String(Math.min(node.scrollHeight, max))}px`;
    node.style.overflowY = node.scrollHeight > max ? 'auto' : 'hidden';
  }, [autoGrow, maxRows]);

  // Слой раскладки, а не эффект: иначе поле успевает мелькнуть неверной
  // высотой между отрисовкой и пересчётом.
  useLayoutEffect(() => {
    resize();
  }, [resize, value]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLength(event.target.value.length);
    resize();
    onChange?.(event);
  };

  return (
    <div className={styles['textarea']}>
      <textarea
        {...rest}
        ref={setRefs}
        id={id ?? field?.controlId}
        className={cx(
          styles['textarea__control'],
          isInvalid && styles['textarea__control--invalid'],
          autoGrow && styles['textarea__control--grow'],
          className,
        )}
        rows={rows}
        disabled={isDisabled}
        required={required ?? field?.required}
        maxLength={maxLength}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        aria-invalid={isInvalid || undefined}
        aria-describedby={ariaDescribedBy ?? field?.describedBy}
      />

      {showCount && maxLength !== undefined && (
        <span
          className={cx(
            styles['textarea__counter'],
            currentLength >= maxLength && styles['textarea__counter--full'],
          )}
        >
          {currentLength}/{maxLength}
        </span>
      )}
    </div>
  );
}
